/**
 * LightPool — a FIXED set of dynamic point lights, handed out per frame.
 *
 * Light is what sells a shot: a bullet that lights the deck as it flies, a
 * muzzle that throws 90 ms of colour onto the floor, an explosion that turns
 * the room its own colour for half a second. The obvious way — a `PointLight`
 * per effect, added and removed as they live and die — is the one thing this
 * renderer punishes: `LightsNode` hashes the set of light ids into every lit
 * material's cache key, so every add or remove is a shader rebuild for the
 * whole scene. On WebGPU that is a visible hitch on exactly the frame the
 * effect fires.
 *
 * So the pool is allocated ONCE, at boot, at the largest size the device's
 * max tier allows, and never grows or shrinks. Every frame, anything that wants
 * light *requests* one (position, colour, intensity, distance); the pool scores
 * the requests and gives its slots to the strongest, weighted toward the
 * camera's focus. Losers simply are not lit this frame. A slot that wins
 * nothing sits at intensity 0 — same shader, same light count, no rebuild.
 * Slots beyond the CURRENT tier's budget are hidden instead (`setActive`): that
 * is one rebuild when the tier changes, not a dead light in every fragment.
 *
 * `flash()` is the one-shot form: a request that re-submits itself every frame
 * with a quadratic decay, for muzzles and impacts that outlive the call.
 *
 * Nothing here allocates per frame or per request.
 */

import * as THREE from 'three/webgpu';
import { uniform } from 'three/tsl';
import { softCircle } from './ParticleUtils.js';

/** Floats per request: x y z r g b intensity distance. */
const REQ = 8;
/** Floats per flash: x y z r g b intensity distance t T. */
const FLASH = 10;

const MAX_REQUESTS = 64;
const MAX_FLASHES = 32;

/** Beyond this distance from the focus a request scores almost nothing. */
const FOCUS_RANGE = 36;

/**
 * A fake light: an additive glow sprite where a real light would be. It does
 * not light anything, but from 55° above it reads as the pool of light a
 * muzzle throws, for the cost of a few dozen fragments. The phone tiers draw
 * these for the requests that do not win one of their (few) real lights.
 */
function createFakeLight() {
  const mat = new THREE.SpriteNodeMaterial();
  const tint = uniform(new THREE.Color(1, 1, 1));
  const fade = uniform(0);
  mat.colorNode = tint;
  mat.opacityNode = softCircle().mul(fade);
  mat.transparent = true;
  mat.depthWrite = false;
  mat.blending = THREE.AdditiveBlending;
  mat.fog = false;
  const sprite = new THREE.Sprite(mat);
  sprite.visible = false;
  sprite.frustumCulled = false;
  sprite.renderOrder = 5;
  sprite.name = 'light-pool:fake';
  return { sprite, tint, fade };
}
/** How bright a fake gets per unit of (gain-scaled) intensity. */
const FAKE_ALPHA_PER_CD = 0.35;
/** The sprite's diameter as a fraction of the light's distance. */
const FAKE_SIZE_PER_DISTANCE = 0.8;

export class LightPool {
  /**
   * @param {number} size how many lights to allocate; fixed for the session
   * @param {object} [opts]
   * @param {number} [opts.gain] multiplies every requested intensity. The
   *   recipes quote intensities in the reference's units (a muzzle is 6.5, an
   *   explosion 48); this scene's toon deck under a 2.1 key and 1.25 exposure
   *   whites out at a third of that, so the pool scales them once, here.
   */
  constructor(size, { gain = 1, fakes = 0 } = {}) {
    this.gain = gain;
    this.size = Math.max(0, Math.floor(size));
    this.active = this.size;
    this.fakes = [];
    for (let i = 0; i < Math.max(0, Math.floor(fakes)); i++) this.fakes.push(createFakeLight());
    this.fakeActive = this.fakes.length;
    this.lights = [];
    for (let i = 0; i < this.size; i++) {
      const light = new THREE.PointLight(0xffffff, 0, 5, 2);
      light.name = `light-pool:${i}`;
      light.position.set(0, -50, 0);
      light.castShadow = false;
      this.lights.push(light);
    }
    this.req = new Float32Array(MAX_REQUESTS * REQ);
    this.n = 0;
    this.flashes = new Float32Array(MAX_FLASHES * FLASH);
    this.flashCursor = 0;
    this._score = new Float32Array(MAX_REQUESTS);
    this._order = new Int32Array(MAX_REQUESTS);
    this.focusX = 0;
    this.focusZ = 0;
    this.stats = { requested: 0, assigned: 0, fakesAssigned: 0, dropped: 0, flashesLive: 0 };
  }

  attach(scene) {
    for (const light of this.lights) scene.add(light);
    for (const f of this.fakes) scene.add(f.sprite);
    return this;
  }

  /** How many real slots, and how many glow sprites, this tier may use. */
  setActive(n, fakes = 0) {
    this.active = Math.max(0, Math.min(this.size, Math.floor(n)));
    // Slots past this tier's budget are HIDDEN, not just dark: a light at
    // intensity 0 still runs in every lit fragment's light loop, so a phone
    // booted at medium was paying for the high tier's six. Hidden lights leave
    // the render list, which costs one shader rebuild per tier change — rare —
    // instead of a per-fragment cost every frame.
    for (let i = 0; i < this.size; i++) {
      this.lights[i].visible = i < this.active;
      if (i >= this.active) this.lights[i].intensity = 0;
    }
    this.fakeActive = Math.max(0, Math.min(this.fakes.length, Math.floor(fakes)));
    for (let i = this.fakeActive; i < this.fakes.length; i++) this.fakes[i].sprite.visible = false;
  }

  /** Where the camera is looking; requests near it win ties. */
  setFocus(x, z) {
    this.focusX = x;
    this.focusZ = z;
  }

  /** A this-frame request. Cleared by `update()`. */
  request(x, y, z, r, g, b, intensity, distance) {
    this._requestRaw(x, y, z, r, g, b, intensity * this.gain, distance);
  }

  _requestRaw(x, y, z, r, g, b, intensity, distance) {
    if ((this.size === 0 && this.fakes.length === 0) || intensity <= 0) return;
    this.stats.requested++;
    let i = this.n;
    if (i >= MAX_REQUESTS) {
      // Full: replace the weakest if this one is stronger, else drop it.
      let weakest = 0;
      for (let k = 1; k < MAX_REQUESTS; k++) {
        if (this.req[k * REQ + 6] < this.req[weakest * REQ + 6]) weakest = k;
      }
      if (this.req[weakest * REQ + 6] >= intensity) {
        this.stats.dropped++;
        return;
      }
      i = weakest;
    } else {
      this.n++;
    }
    const o = i * REQ;
    const q = this.req;
    q[o] = x;
    q[o + 1] = y;
    q[o + 2] = z;
    q[o + 3] = r;
    q[o + 4] = g;
    q[o + 5] = b;
    q[o + 6] = intensity;
    q[o + 7] = distance;
  }

  /** A decaying request that lives `durationS` seconds. Overwrites the oldest when full. */
  flash(x, y, z, r, g, b, intensity, distance, durationS) {
    if (this.size === 0 && this.fakes.length === 0) return;
    intensity *= this.gain;
    const i = this.flashCursor;
    this.flashCursor = (i + 1) % MAX_FLASHES;
    const o = i * FLASH;
    const f = this.flashes;
    f[o] = x;
    f[o + 1] = y;
    f[o + 2] = z;
    f[o + 3] = r;
    f[o + 4] = g;
    f[o + 5] = b;
    f[o + 6] = intensity;
    f[o + 7] = distance;
    f[o + 8] = 0;
    f[o + 9] = Math.max(1e-3, durationS);
  }

  /**
   * Advance the flashes, score the requests, assign the slots, forget the
   * requests. `dt` is the presentation delta: 0 during hitstop keeps a muzzle
   * flash lit through the freeze, which is exactly the frame it should be lit.
   */
  update(dt) {
    const f = this.flashes;
    let flashesLive = 0;
    for (let i = 0; i < MAX_FLASHES; i++) {
      const o = i * FLASH;
      const T = f[o + 9];
      if (T <= 0) continue;
      const t = f[o + 8] + dt;
      if (t >= T) {
        f[o + 9] = 0;
        continue;
      }
      f[o + 8] = t;
      flashesLive++;
      const k = 1 - t / T;
      this._requestRaw(f[o], f[o + 1], f[o + 2], f[o + 3], f[o + 4], f[o + 5], f[o + 6] * k * k, f[o + 7]);
    }
    this.stats.flashesLive = flashesLive;

    const n = this.n;
    // Flashes re-entered above through _requestRaw, already scaled by flash().
    const q = this.req;
    const score = this._score;
    const order = this._order;
    for (let i = 0; i < n; i++) {
      const o = i * REQ;
      const dx = q[o] - this.focusX;
      const dz = q[o + 2] - this.focusZ;
      const d = Math.sqrt(dx * dx + dz * dz);
      score[i] = q[o + 6] * Math.max(0.08, 1 - d / FOCUS_RANGE);
      order[i] = i;
    }
    // Partial selection: the top requests, strongest first — the real slots
    // and then the fakes. n ≤ 64 and the slots ≤ 16, so ~1000 compares at most.
    const winners = Math.min(this.active, n);
    const fakeWinners = Math.min(this.fakeActive, Math.max(0, n - winners));
    for (let w = 0; w < winners + fakeWinners; w++) {
      let best = w;
      for (let k = w + 1; k < n; k++) {
        if (score[order[k]] > score[order[best]]) best = k;
      }
      const tmp = order[w];
      order[w] = order[best];
      order[best] = tmp;
    }
    for (let slot = 0; slot < this.size; slot++) {
      const light = this.lights[slot];
      if (slot < winners) {
        const o = order[slot] * REQ;
        light.position.set(q[o], q[o + 1], q[o + 2]);
        light.color.setRGB(q[o + 3], q[o + 4], q[o + 5]);
        light.intensity = q[o + 6];
        light.distance = q[o + 7];
      } else if (light.intensity !== 0) {
        light.intensity = 0;
      }
    }
    for (let k = 0; k < this.fakes.length; k++) {
      const fake = this.fakes[k];
      if (k < fakeWinners) {
        const o = order[winners + k] * REQ;
        fake.sprite.position.set(q[o], q[o + 1], q[o + 2]);
        fake.sprite.scale.setScalar(q[o + 7] * FAKE_SIZE_PER_DISTANCE);
        // HDR tint so it blooms on medium; the alpha carries the intensity.
        fake.tint.value.setRGB(q[o + 3] * 2.5, q[o + 4] * 2.5, q[o + 5] * 2.5);
        fake.fade.value = Math.min(1, q[o + 6] * FAKE_ALPHA_PER_CD);
        fake.sprite.visible = true;
      } else if (fake.sprite.visible) {
        fake.sprite.visible = false;
      }
    }
    this.stats.assigned = winners;
    this.stats.fakesAssigned = fakeWinners;
    this.n = 0;
  }

  clear() {
    this.n = 0;
    for (let i = 0; i < MAX_FLASHES; i++) this.flashes[i * FLASH + 9] = 0;
    for (const light of this.lights) light.intensity = 0;
    for (const f of this.fakes) f.sprite.visible = false;
  }

  dispose() {
    for (const light of this.lights) {
      light.removeFromParent();
      light.dispose();
    }
    this.lights.length = 0;
    for (const f of this.fakes) {
      f.sprite.removeFromParent();
      f.sprite.material.dispose();
    }
    this.fakes.length = 0;
  }
}
